<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex flex-row">
          <McButton
            class="me-2"
            variant="secondary"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            @click="handleBack"
          />
        </div>
      </div>
    </div>
    <div class="clearfix mb-4">
      <h4 class="card-title float-start">
        {{ $t('maintenance.frontPage.title.infoServiceInt') }}
      </h4>
      <div class="float-end d-flex flex-row gap-2">
        <McButton
          variant="secondary"
          disabled
          :text="$t('global.cancel')"
          icon="icon-outline_close"
          :loading="isLoading"
          @click="handleCancel"
        />
        <McButton
          variant="primary"
          icon="icon-outline_save"
          :text="$t('global.save')"
          :disabled="isSaveDisabled"
          :loading="isLoading"
          @click="handleSave"
        />
      </div>
    </div>

    <FormulateForm ref="formRef" v-model="form" @validation="handleValidation">
      <b-card>
        <b-row>
          <b-col md="3" class="mb-3">
            <FormulateInput
              type="value-help"
              name="licensePlateSelect"
              :placeholder="`${$t('global.form.select')} ${$t('global.object.licensePlate')}`"
              :listGetter="resourcefulQuery.licensePlate"
              :tableFields="tableFields.licensePlate"
              :trackBy="'id'"
              :displayedBy="'licensePlate'"
              :initialObject="initialObject.vehicle"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              :modalTitle="$t('global.object.licensePlate')"
              :visibleValidationErrors="true"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('global.object.licensePlate') }} <i class="text-danger">*</i></label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col md="3" class="mb-3">
            <FormulateInput type="group" :label="$t('maintenance.order.serviceLocationType')">
              <div class="mt-2">
                <div class="form-check form-check-inline">
                  <input
                    id="internal"
                    v-model="form.locationType"
                    class="form-check-input"
                    type="radio"
                    name="locationTypeOptions"
                    :value="'Internal'"
                    @click="positionName = ['Workshop']"
                  />
                  <label class="form-check-label" for="internal">{{
                    $t('maintenance.order.form.internal')
                  }}</label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    id="eksternal"
                    v-model="form.locationType"
                    class="form-check-input"
                    type="radio"
                    :value="'Eksternal'"
                    name="locationTypeOptions"
                    @click="positionName = ['Workshop', 'Driver']"
                  />
                  <label class="form-check-label" for="eksternal">{{
                    $t('maintenance.order.form.external')
                  }}</label>
                </div>
              </div>
            </FormulateInput>
          </b-col>
          <b-col md="3" class="mb-3">
            <FormulateInput
              v-model="form.serviceLocationSelect"
              type="select-resourceful-v2"
              name="serviceLocationSelect"
              :placeholder="`${$t('global.form.select')} ${$t(
                'maintenance.order.serviceLocation'
              )}`"
              :trackBy="'geofenceId'"
              :optionsGetter="resourcefulQuery.serviceLocation"
              :optionLabelFn="({ item }) => item.fenceName"
              :fullObject="fullObject.serviceLocation"
              validation="required:trim"
              error-behavior="blur"
              validation-behavior="blur"
              :multiple="false"
              :taggable="form.locationType == 'Eksternal' ? true : false"
              tabIndex="0"
              @addTag="tagCustom"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>
                    {{ $t('maintenance.order.serviceLocation') }}
                    <i class="text-danger">*</i>
                  </label>
                </div>
              </template>
            </FormulateInput>
          </b-col>

          <b-col md="3">
            <FormulateInput
              type="select-resourceful"
              name="pic"
              :placeholder="`${$t('global.form.select')} ${$t('maintenance.order.form.pic')}`"
              :optionsGetter="resourcefulQuery.employees"
              :trackBy="'userId'"
              :optionLabelFn="({ item }) => item.fullname"
              :initialObject="initialObject.pic"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              tabIndex="0"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('maintenance.order.form.pic') }} <i class="text-danger">*</i></label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col v-if="isMGMAndMO" md="3">
            <FormulateInput
              type="text"
              name="mechanicName"
              :placeholder="$t('maintenance.order.form.mechanicNamePlaceholder')"
              validation="required|max:128,length"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              tabIndex="0"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>
                    {{ $t('maintenance.order.form.mechanicName') }}
                    <i class="text-danger">*</i>
                  </label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col md="3">
            <FormulateInput
              name="startServicePeriode"
              :label="$t('maintenance.order.form.startDate')"
              :placeholder="$t('maintenance.order.form.autoInProgressPlaceholder')"
              type="date-picker"
              :range="true"
              :format="'DD MMM YYYY'"
              :value-type="'YYYY-MM-DD'"
              :disabled="true"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              name="etaServicePeriode"
              :label="$t('maintenance.order.form.etaTime')"
              :placeholder="$t('maintenance.order.form.enableInprogressPlaceholder')"
              type="date-picker"
              :range="true"
              :format="'DD MMM YYYY'"
              :value-type="'YYYY-MM-DD'"
              :disabled="true"
            />
          </b-col>
          <b-col v-if="isMGMAndMO" md="3">
            <FormulateInput
              class="maintenance-type-input"
              type="select-resourceful"
              name="maintenanceType"
              trackBy="value"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              :multiple="true"
              :label="$t('maintenance.order.form.maintenanceType')"
              :placeholder="$t('maintenance.order.form.maintenanceTypePlaceholder')"
              :optionsGetter="resourcefulQuery.maintenanceTypes"
              :optionLabelFn="({ item }) => item.label"
              :searchable="false"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label
                    >{{ $t('maintenance.order.form.maintenanceType') }}
                    <i class="text-danger">*</i></label
                  >
                </div>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import { GET_GEOFENCES } from '@/services/api/master/geofence'
import { GET_EMPLOYEES_PAGINATED } from '@/services/api/master/employees'
import { CREATE_SERVICE_ORDER } from '@/services/api/maintenance/serviceorder'
import McButton from '@/components/McButton'
import { BULK_CREATE_SERVICE_TO_SERVICE_ORDER } from '@/services/api/maintenance/service2serviceorder'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
  },

  mixins: [Helper],

  apollo: {
    vehiclesPaginated: {
      query: GET_VEHICLES_PAGINATED,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 999999,
            },
            parentOnly: true,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        if (data) this.vehicleList = data.vehiclesPaginated.vehiclesPaginated.data
      },
      skip() {
        return !this.$route.query
      },
    },
  },

  data() {
    return {
      isLoading: false,
      form: {
        licensePlateSelect: null,
        serviceLocationSelect: null,
        locationType: 'Internal',
        pic: null,
        startServicePeriode: '',
        etaServicePeriode: '',
      },
      rendered: true,
      isUpdate: false,
      initialObject: {
        pic: null,
        serviceLocation: null,
        vehicle: null,
      },
      serviceLocation: [],
      positionName: ['Workshop'],
      oldValues: {},
      newValues: {},
      isDisabled: false,
      serviceLocationKey: 'geofenceId',
      selectedServiceLocation: null,
      fullObject: {
        serviceLocation: {},
      },
      shortcutData: this.$store.state.maintenance.shortcutMO,
      vehicleList: [],
      errorObj: {
        hasErrors: true,
      },
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    title() {
      return this.$t('global.create') + ' ' + this.$t('maintenance.order.form.newSO')
    },

    items() {
      return [
        {
          text: this.$t('maintenance.title'),
          to: { name: 'Service' },
        },
        {
          text: this.$t('maintenance.frontPage.title.serviceOrder'),
          to: { name: 'Service Order List' },
        },
        {
          text: this.$t('global.create') + ' ' + this.$t('maintenance.order.form.newSO'),
        },
      ]
    },

    resourcefulQuery() {
      return {
        maintenanceTypes: () => ({
          metadata: {
            total_count: 3,
            page: 1,
          },
          data: {
            paginated_result: [
              { value: 'Breakdown', label: 'Breakdown' },
              { value: 'General Issue / Routine', label: 'General Issue / Routine' },
              { value: 'Preventive', label: 'Preventive' },
            ],
          },
          isPagination: false,
        }),
        licensePlate: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            parentOnly: true,
            orderBy: opts.sorts,
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VEHICLES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehiclesPaginated.vehiclesPaginated

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },

        serviceLocation: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_GEOFENCES,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                isServiceLoc: true,
                serviceLocType: this.form.locationType ?? ['Internal', 'Eksternal'],
              },
            },
          })

          const locationOptions = res.data.geofences.geofences
          const self = this
          self.serviceLocation = locationOptions.data
          self.initialObject.serviceLocation = self.serviceLocation

          result = {
            metadata: {
              total_count: locationOptions.pagination.total,
              page: locationOptions.pagination.page,
            },
            data: {
              paginated_result: locationOptions.data,
            },
          }

          return result
        },

        employees: async (opts) => {
          let result = {}
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_EMPLOYEES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                positionName: this.positionName,
              },
            },
          })

          const employees = res.data.employeesPaginated.employeesPaginated

          result = {
            metadata: {
              total_count: employees.pagination.total,
              page: employees.pagination.page,
            },
            data: {
              paginated_result: employees.data,
            },
          }

          return result
        },
      }
    },

    tableFields() {
      return {
        licensePlate: [
          {
            key: 'licensePlate',
            label: this.$t('global.object.licensePlate'),
          },
          {
            key: 'vehicleType.type',
            label: this.$t('global.object.fleetType'),
          },
          {
            key: 'vehicleType.category',
            label: this.$t('global.object.fleetCategory'),
          },
          {
            key: 'vehicleType.model',
            label: this.$t('global.object.fleetModel'),
          },
        ],
      }
    },

    isShortcutDataEmpty() {
      return Object.keys(this.shortcutData).length === 0
    },

    isSaveDisabled() {
      const hasValue = (value) => value !== null && value !== undefined && value !== ''
      const hasRequiredBaseFields =
        hasValue(this.form.licensePlateSelect) &&
        hasValue(this.form.serviceLocationSelect) &&
        hasValue(this.form.pic) &&
        hasValue(this.selectedServiceLocation)

      if (!hasRequiredBaseFields) return true

      if (this.isMGMAndMO) {
        const hasMechanicName = hasValue(this.form.mechanicName)
        const exceedMaxMechanicName = this.form.mechanicName && this.form.mechanicName.length > 128
        const hasMaintenanceType =
          Array.isArray(this.form.maintenanceType) && this.form.maintenanceType.length > 0

        if (!hasMechanicName || exceedMaxMechanicName || !hasMaintenanceType) return true
      }

      return this.errorObj.hasErrors
    },
  },

  watch: {
    'form.locationType': {
      handler(newValue, oldValue) {
        this.positionName = newValue === 'Internal' ? ['Workshop'] : ['Workshop', 'Driver']

        if (oldValue === null || oldValue === undefined || newValue === oldValue) return

        this.form.serviceLocationSelect = null
        this.selectedServiceLocation = null
        this.fullObject.serviceLocation = null
        this.serviceLocationKey = 'geofenceId'
      },
    },

    'form.serviceLocationSelect': {
      deep: true,
      handler(newValue, oldValue) {
        const selected = this.serviceLocation.filter((item) => item.geofenceId === newValue)
        this.fullObject.serviceLocation = selected[0]

        this.selectedServiceLocation = selected[0]

        if (newValue !== 0) this.serviceLocationKey = 'geofenceId'
        else this.serviceLocationKey = 'address'
      },
    },
  },

  mounted() {
    if (!this.isShortcutDataEmpty) {
      this.initialObject.vehicle = this.shortcutData.vehicle
      this.form.licensePlateSelect = this.shortcutData.vehicle.id
    }
  },

  methods: {
    handleValidation(error) {
      this.errorObj = error
    },

    licensePlateModal() {
      this.$bvModal.show('modal-license-plate-service')
    },

    handleBack() {
      this.$router.push({
        name: 'Service Order List',
      })
    },

    handleCancel() {
      this.form = {
        licensePlateSelect: null,
        serviceLocationSelect: null,
        locationType: null,
        pic: null,
        startServicePeriode: '',
        etaServicePeriode: '',
      }
    },

    tagCustom(newTag) {
      this.serviceLocation = this.serviceLocation.filter((item) => item.geofenceId !== 0)

      const tag = {
        address: newTag,
        fenceName: newTag,
        geofenceId: 0,
      }

      this.serviceLocation.push(tag)
      this.serviceLocationSelected = tag
      this.fullObject.serviceLocation = tag

      this.form.serviceLocationSelect = tag.geofenceId
    },

    async handleSave() {
      this.isLoading = true
      const input = {
        [this.serviceLocationKey]: this.selectedServiceLocation[this.serviceLocationKey],
        locationType: this.form.locationType,
        picId: this.form.pic,
        mechanicName: this.form.mechanicName,
        maintenanceType: this.form.maintenanceType,
        vehicleId: +this.form.licensePlateSelect,
        manualAssignService: !this.isShortcutDataEmpty ? true : false,
      }

      const res = await this.$apollo.mutate({
        mutation: CREATE_SERVICE_ORDER,
        variables: {
          input: input,
        },
      })

      if (res.data.createServiceOrder.isSuccessful) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'strong',
                text: res.data.createServiceOrder.createServiceOrder.code,
                class: '__blue-highlight-text',
              },
              { tag: 'span', text: this.$t('maintenance.order.text.savedAs') },
              { tag: 'strong', text: this.$t('maintenance.order.filter.draft'), class: '__status' },
            ],
          },
        })

        if (!this.isShortcutDataEmpty) {
          const services = this.shortcutData.services.map((item) => ({
            serviceId: item.serviceId,
            serviceType: 'Schedule',
            serviceScheduleType: item.type,
          }))

          const update = await this.$apollo.mutate({
            mutation: BULK_CREATE_SERVICE_TO_SERVICE_ORDER,
            variables: {
              input: {
                serviceOrderId: res.data.createServiceOrder.createServiceOrder.serviceOrderId,
                services: services,
              },
            },
          })

          if (update.data.createServices2ServiceOrder.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.added'))
            this.$store.commit('maintenance/SET_SHORTCUT_MO', null)
          } else {
            this.$mcToast.error(update.data.createServices2ServiceOrder.message)
          }
        }
        this.$router.push({
          name: 'Maintenance Detail Service Order',
          params: { id: res.data.createServiceOrder.createServiceOrder.serviceOrderId },
        })
      }
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss">
.maintenance-type-input {
  .formulate-input-element--select-resourceful,
  .multiselect__tags {
    height: auto;
    min-height: 37px !important;
  }

  .multiselect__tags {
    max-height: none;
  }
}
</style>
